import { useState, useEffect } from 'react';
import { getUserProfile } from '../services/userProfileService';

export interface WalletUserData {
    id: string;
    name: string;
    walletAddress: string;
    avatar: string;
    walletProvider: string;
    isLoading: boolean;
    error: string | null;
}

// Simple in-memory cache to prevent redundant API calls across components
const userDataCache = new Map<string, { data: WalletUserData; timestamp: number }>();
const CACHE_DURATION = 30000; // 30 seconds

/**
 * Optimized hook to get current user data from the connected wallet
 * Leverages existing wallet connection state instead of redundant detection
 */
export function useWalletUser(): WalletUserData {
    const [userData, setUserData] = useState<WalletUserData>({
        id: '',
        name: 'EKKO Trader',
        walletAddress: '',
        avatar: 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3.png?auto=compress&cs=tinysrgb&w=100',
        walletProvider: 'unknown',
        isLoading: true,
        error: null
    });

    useEffect(() => {
        let mounted = true;

        const fetchUserData = async () => {
            try {
                // Check for wallet connection from existing wallet state sources
                let currentWallet: string | null = null;
                let walletProvider: string = 'unknown';

                // First check localStorage (fastest)
                const savedWalletAddress = localStorage.getItem('walletAddress');
                if (savedWalletAddress) {
                    currentWallet = savedWalletAddress;
                }

                // Then check window wallet objects if no saved address
                if (!currentWallet) {
                    if (window.solana?.isConnected && window.solana.publicKey) {
                        currentWallet = window.solana.publicKey.toString();
                        walletProvider = 'phantom';
                    } else if (window.solflare?.isConnected && window.solflare.publicKey) {
                        currentWallet = window.solflare.publicKey.toString();
                        walletProvider = 'solflare';
                    }
                }

                if (!currentWallet) {
                    if (mounted) {
                        setUserData(prev => ({
                            ...prev,
                            isLoading: false,
                            error: null // Don't treat no wallet as an error
                        }));
                    }
                    return;
                }

                // Check cache first
                const cached = userDataCache.get(currentWallet);
                if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
                    if (mounted) {
                        setUserData(cached.data);
                    }
                    return;
                }

                // Get user profile (this handles user creation if needed)
                const profile = await getUserProfile(currentWallet);

                const displayName = profile?.display_name ||
                    currentWallet.slice(0, 6) + '...' + currentWallet.slice(-4);
                const avatarUrl = profile?.avatar_url || userData.avatar;

                const newUserData: WalletUserData = {
                    id: profile?.id || '',
                    name: displayName,
                    walletAddress: currentWallet,
                    avatar: avatarUrl,
                    walletProvider,
                    isLoading: false,
                    error: null
                };

                // Cache the result
                userDataCache.set(currentWallet, {
                    data: newUserData,
                    timestamp: Date.now()
                });

                if (mounted) {
                    setUserData(newUserData);
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
                if (mounted) {
                    setUserData(prev => ({
                        ...prev,
                        isLoading: false,
                        error: error instanceof Error ? error.message : 'Unknown error'
                    }));
                }
            }
        };

        // Initial load
        fetchUserData();

        // Listen for wallet connection events instead of polling
        const handleWalletChange = () => {
            fetchUserData();
        };

        window.addEventListener('ekko:wallet_connected', handleWalletChange);
        window.addEventListener('ekko:wallet_disconnected', handleWalletChange);
        window.addEventListener('ekko:wallet_selected', handleWalletChange);

        return () => {
            mounted = false;
            window.removeEventListener('ekko:wallet_connected', handleWalletChange);
            window.removeEventListener('ekko:wallet_disconnected', handleWalletChange);
            window.removeEventListener('ekko:wallet_selected', handleWalletChange);
        };
    }, []);

    return userData;
} 